import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  productList,
  productDetail,
  createProduct,
  updateProduct,
  approveProduct,
  deleteProduct,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";
import { IProduct } from "../../../types/api/product";
import { TResponse, TResponseWithQData } from "../../../types/common";

export const PRODUCT_KEYS = {
  all: ["products"] as const,
  lists: () => [...PRODUCT_KEYS.all, "list"] as const,
  list: (qData: QData) => [...PRODUCT_KEYS.lists(), qData] as const,
  details: () => [...PRODUCT_KEYS.all, "detail"] as const,
  detail: (id: string) => [...PRODUCT_KEYS.details(), id] as const,
};

export function useProductList(
  qData: QData & { artistId?: string; search?: string },
  options?: any
) {
  const [extraQuery, setExtraQuery] = useState<Record<string, any>>({});

  const mergedQData = useMemo(() => {
    return { ...qData, ...extraQuery };
  }, [qData, extraQuery]);

  const query = useQuery<TResponseWithQData<IProduct[]>>({
    queryKey: PRODUCT_KEYS.list(mergedQData),
    queryFn: async () => {
      const { data } = await productList(mergedQData);
      return data;
    },
    ...options,
  });

  const refetchWithQuery = useCallback((newQData: typeof qData) => {
    setExtraQuery(newQData);
  }, []);

  return { ...query, refetchWithQuery };
}

export function useProductDetail(id: string, options?: any) {
  return useQuery<TResponse<IProduct>>({
    queryKey: PRODUCT_KEYS.detail(id),
    queryFn: async () => {
      const { data } = await productDetail(id);
      return data;
    },
    enabled: !!id,
    ...options,
  });
}

export function useProductCreate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProduct,
    onSuccess: (res) => {
      if (qData) {
        queryClient.setQueryData(
          PRODUCT_KEYS.list(qData),
          (old: TResponseWithQData<IProduct[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: [res.data.data, ...(old.data || [])],
              extra: {
                ...old.extra,
                total: (old.extra?.total || 0) + 1,
              },
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError,
  });
}

export function useProductUpdate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProduct,
    onSuccess: (res) => {
      const updatedProduct = res.data.data;
      if (qData) {
        queryClient.setQueryData(
          PRODUCT_KEYS.list(qData),
          (old: TResponseWithQData<IProduct[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.map((item) =>
                item._id === updatedProduct._id ? updatedProduct : item
              ),
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.lists() });
      }
      queryClient.setQueryData(PRODUCT_KEYS.detail(updatedProduct._id), (old: any) => {
        if (!old) return old;
        return { ...old, data: updatedProduct };
      });
      onSuccess?.();
    },
    onError,
  });
}

export function useProductApprove({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveProduct,
    onSuccess: (res) => {
      const updatedProduct = res.data.data;
      if (qData) {
        queryClient.setQueryData(
          PRODUCT_KEYS.list(qData),
          (old: TResponseWithQData<IProduct[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.map((item) =>
                item._id === updatedProduct._id ? updatedProduct : item
              ),
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.lists() });
      }
      queryClient.setQueryData(PRODUCT_KEYS.detail(updatedProduct._id), (old: any) => {
        if (!old) return old;
        return { ...old, data: updatedProduct };
      });
      onSuccess?.();
    },
    onError,
  });
}

export function useProductDelete({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: (_, deletedId) => {
      if (qData) {
        queryClient.setQueryData(
          PRODUCT_KEYS.list(qData),
          (old: TResponseWithQData<IProduct[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.filter((item) => item._id !== deletedId),
              extra: {
                ...old.extra,
                total: Math.max(0, (old.extra?.total || 1) - 1),
              },
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError,
  });
}
